import { useState } from "react";
import { Col, Input, Label, Modal, Row } from "reactstrap";
import { CrossSectionTarget } from "src/helpers/metricsLinkMap";
import "./CrossSectionSyncModal.css";

interface CrossSectionSyncModalProps {
  isOpen: boolean;
  metricLabel: string;
  action: "select" | "deselect";
  originLabel: string;
  targets: CrossSectionTarget[];
  onConfirm: (selectedTargets: CrossSectionTarget[]) => void;
  onDismiss: () => void;
  onCancel: () => void;
}

const CrossSectionSyncModal = ({
  isOpen,
  metricLabel,
  action,
  originLabel,
  targets,
  onConfirm,
  onDismiss,
  onCancel,
}: CrossSectionSyncModalProps) => {
  const [checkedIndices, setCheckedIndices] = useState<Set<number>>(new Set());

  const handleToggleIndex = (index: number) => {
    setCheckedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = targets.filter((_, i) => checkedIndices.has(i));
    setCheckedIndices(new Set());
    onConfirm(selected);
  };

  const handleDismiss = () => {
    setCheckedIndices(new Set());
    onDismiss();
  };

  const handleCancel = () => {
    setCheckedIndices(new Set());
    onCancel();
  };

  const allSelected = targets.length > 0 && checkedIndices.size === targets.length;

  const handleToggleAll = () => {
    if (allSelected) {
      setCheckedIndices(new Set());
    } else {
      setCheckedIndices(new Set(targets.map((_, i) => i)));
    }
  };

  const actionLabel = action === "select" ? "selecting" : "deselecting";

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} centered={true} size="md">
      <button
        onClick={handleCancel}
        type="button"
        className="close position-absolute confirm-modal-close"
        data-dismiss="modal"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div className="modal-body sync-modal-body">
        <div className="text-center sync-modal-header">
          <i className="mdi mdi-sync confirm-modal-icon" />
          <h5 className="sync-modal-title">Sync Metric?</h5>
        </div>
        <div className="sync-modal-description">
          <p className="mb-1">
            You are {actionLabel} <strong>{metricLabel}</strong> in <strong>{originLabel}</strong>.
          </p>
          <p className="text-muted mb-0">
            This metric also exists in the following sections. Select the ones you want to sync {action === "select" ? "selection" : "deselection"} with:
          </p>
        </div>
        <div className="cross-section-sync-list">
          <div className="cross-section-sync-item sync-toggle-all-item">
            <Label check className="d-flex align-items-center gap-2 mb-0 sync-item-label sync-toggle-all-label">
              <Input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
              />
              <span>Select All</span>
            </Label>
          </div>
          {targets.map((target, index) => (
            <div key={index} className="cross-section-sync-item">
              <Label check className="d-flex align-items-center gap-2 mb-0 sync-item-label">
                <Input
                  type="checkbox"
                  checked={checkedIndices.has(index)}
                  onChange={() => handleToggleIndex(index)}
                />
                <span>{target.label}</span>
              </Label>
            </div>
          ))}
        </div>
        <hr className="sync-modal-divider" />
        <Row className="g-2 justify-content-center">
          <Col xs="auto">
            <button
              type="button"
              className="btn btn-primary button-primary sync-modal-btn"
              onClick={handleConfirm}
            >
              Yes, Sync
            </button>
          </Col>
          <Col xs="auto">
            <button
              type="button"
              className="btn btn-danger button-secondary confirm-modal-cancel sync-modal-btn"
              onClick={handleDismiss}
            >
              No, Skip
            </button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default CrossSectionSyncModal;
